// The drugs given on one incident (models/drug_administration.py), with the two
// corrections a record allows.
//
// A record is never edited into saying something else: the notes may be corrected,
// but a wrong drug, batch, route, dose or amount is *voided* with a reason — which
// puts back the stock it took — and recorded again. A voided record stays on the
// list, struck through, with who voided it and why.
import { useState } from "react";
import axios from "axios";
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
  Stack, TextField, Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import BlockIcon from "@mui/icons-material/Block";
import { API_BASE_URL } from "../config";
import { notify } from "../notify";
import { fmtDateTime } from "../incidentLabels";
import { drugLine, stockText } from "../incidentNotes";

const INCIDENTS_API = `${API_BASE_URL}/incidents`;

const fmtDate = (iso) => {
  if (!iso) return "no expiry date";
  const [y, m, d] = String(iso).slice(0, 10).split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(undefined,
    { day: "numeric", month: "short", year: "numeric" });
};

export default function DrugAdministrationList({ drugs, incidentId, canEdit, onChanged }) {
  const [editing, setEditing] = useState(null);   // { id, notes }
  const [voiding, setVoiding] = useState(null);   // { id, reason }
  const [busy, setBusy] = useState(false);

  const run = (call, message) => {
    setBusy(true);
    call().then(() => {
      notify(message, "success");
      setEditing(null); setVoiding(null);
      onChanged && onChanged();
    }).catch(() => {}).finally(() => setBusy(false));
  };

  const saveNotes = () => run(
    () => axios.put(`${INCIDENTS_API}/${incidentId}/drugs/${editing.id}`,
                    { notes: editing.notes }),
    "Notes corrected.");

  const saveVoid = () => run(
    () => axios.post(`${INCIDENTS_API}/${incidentId}/drugs/${voiding.id}/void`,
                     { reason: voiding.reason }),
    "Record voided, and any stock it took put back.");

  return (
    <>
      <Typography variant="subtitle2">Drugs given ({drugs.filter(d => !d.voided_at).length})</Typography>
      <Stack spacing={1} sx={{ mt: 0.5 }}>
        {drugs.map(d => {
          const voided = !!d.voided_at;
          return (
            <Box key={d.id} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1,
                                  p: 1.5, opacity: voided ? 0.6 : 1 }}>
              <Typography variant="body2"
                          sx={{ fontWeight: 600, textDecoration: voided ? "line-through" : "none" }}>
                {drugLine(d)}
              </Typography>
              {d.notes && (
                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>{d.notes}</Typography>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                From {d.from_label || "—"} ({stockText(d)}) · batch {d.batch_number || "not recorded"}, expires{" "}
                {fmtDate(d.expiry_date)}
              </Typography>
              {!d.stock_taken && (
                <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                  Not taken out of stock: the event had not started yet.
                </Typography>
              )}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                  Given by {d.given_by || "—"}{d.given_by_role ? ` (${d.given_by_role})` : ""} — {fmtDateTime(d.given_at)}
                </Typography>
                {canEdit && !voided && (
                  <>
                    <Button size="small" startIcon={<EditIcon />}
                            onClick={() => setEditing({ id: d.id, notes: d.notes || "" })}>
                      Notes
                    </Button>
                    <Button size="small" color="error" startIcon={<BlockIcon />}
                            onClick={() => setVoiding({ id: d.id, reason: "" })}>
                      Void
                    </Button>
                  </>
                )}
              </Box>
              {voided && (
                <Typography variant="caption" color="error" sx={{ display: "block" }}>
                  Voided by {d.voided_by || "—"} — {fmtDateTime(d.voided_at)}: {d.void_reason}
                </Typography>
              )}
            </Box>
          );
        })}
      </Stack>

      <Dialog open={!!editing} onClose={() => setEditing(null)} fullWidth maxWidth="sm">
        <DialogTitle>Correct the notes</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Only the notes can be corrected. A wrong drug, batch, route, dose or amount
            is voided and recorded again, so the record never quietly changes.
          </DialogContentText>
          <TextField label="Notes" fullWidth multiline minRows={2} autoFocus
                     value={editing?.notes || ""}
                     onChange={e => setEditing(x => ({ ...x, notes: e.target.value }))} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditing(null)}>Cancel</Button>
          <Button variant="contained" disabled={busy} onClick={saveNotes}>Save</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!voiding} onClose={() => setVoiding(null)} fullWidth maxWidth="sm">
        <DialogTitle>Void this record?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Use this when the drug was recorded in error. The record stays on the
            incident, struck through, and any stock it took is put back where it came
            from. If the drug was given but the details are wrong, record it again
            correctly afterwards.
          </DialogContentText>
          <TextField label="Why is it being voided?" fullWidth multiline autoFocus required
                     value={voiding?.reason || ""}
                     onChange={e => setVoiding(x => ({ ...x, reason: e.target.value }))} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVoiding(null)}>Cancel</Button>
          <Button variant="contained" color="error"
                  disabled={busy || !(voiding?.reason || "").trim()} onClick={saveVoid}>
            Void record
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
